/* eslint-disable @typescript-eslint/no-explicit-any */
import * as Icons from "lucide-react";

type IconName = keyof typeof Icons;

import {
  useEffect,
  useState,
  useMemo,
  useEffectEvent,
  useCallback,
  Fragment,
  ReactNode,
} from "react";
import { toast } from "sonner";
import { useCommonI18n } from "@/hooks/useModuleI18n";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Table } from "@/components/ui/table";
import { useColumnVisibility } from "@/hooks/useColumnVisibility";
import { useSorting } from "@/hooks/useSortingTable";
import ColumnVisibilityToggle from "./column-visibility-toggle";
import { TableViewToggle } from "./table-view-toggle";
import { useTableViewMode } from "@/hooks/useTableViewMode";
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  LucideIcon,
  Inbox,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Checkbox } from "@/components/ui/checkbox";
import AdvancedPagination from "./pagination-table";
import SkeletonTableBody from "./skeleton-table-body";
import SkeletonCardsMobile from "./skeleton-cards-mobile";
import SkeletonCardsGrid from "./skeleton-cards-grid";
import {
  TableColumnGeneric as OptimizedTableColumn,
  TableActionGeneric as OptimizedTableAction,
} from "@/hooks/table/types";
import DataTableBody from "./data-table-body";
import DataTableHeader from "./data-table-header";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "../ui/empty";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

// Define the action type properly - this is NOT a ReactNode, it's a configuration object
export interface TableAction<T = any> extends OptimizedTableAction<T> {
  // Esta interfaz extiende OptimizedTableAction para compatibilidad
}

// Define column type
export interface TableColumn<T = any> extends OptimizedTableColumn<T> {
  // Esta interfaz extiende OptimizedTableColumn para compatibilidad
}

// Re-export optimized types for new usage
export type { OptimizedTableColumn, OptimizedTableAction };

export interface DataTablePaginationProps {
  currentPage: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  /** totalCount es una cota inferior (el servidor no contó todas las filas). */
  totalCountIsLowerBound?: boolean;
}

// Define the props for the DataTable
export interface DataTableProps<T = any> {
  icon?: IconName;
  title?: string;
  description?: string;
  columns: TableColumn<T>[];
  data: T[] | Record<string, T[]>;
  actions?: TableAction<T>[];
  rowActions?: (row: T) => TableAction<T>[];
  isLoading?: boolean;
  emptyState?: {
    icon?: ReactNode;
    title: string;
    description: string;
  };
  pagination?: DataTablePaginationProps;
  className?: string;
  // ❌ ELIMINAR: onSort?: (sortConfig: SortConfig) => void;
  enableColumnVisibility?: boolean;
  /** Habilita el selector de vista tabla/tarjetas (requiere tableId para persistir). */
  enableViewToggle?: boolean;
  tableId?: string;
  enableRowSelection?: boolean;
  selectedRows?: Set<string | number>;
  onSelectionChange?: (selectedRows: Set<string | number>) => void;
  rowIdField?: string;
}

export default function DataTable<T extends Record<string, any>>({
  title,
  description,
  columns,
  data,
  actions = [],
  rowActions,
  isLoading,
  emptyState,
  pagination,
  className,
  enableColumnVisibility = false,
  enableViewToggle = true,
  tableId,
  enableRowSelection = false,
  selectedRows: externalSelectedRows,
  onSelectionChange,
  rowIdField = "_id",
  icon,
}: DataTableProps<T>) {
  // Hook de traducción
  const { t } = useCommonI18n();
  
  //  OPTIMIZACIÓN 1: Usar useEffectEvent para el toast
  const showSelectionToast = useEffectEvent(
    (count: number, onClear: () => void, translations: { message: string; clear: string; description: string }) => {
      if (count > 0) {
        toast(
          translations.message,
          {
            id: "row-selection",
            duration: Infinity,
            action: {
              label: translations.clear,
              onClick: onClear,
            },
            description: translations.description,
          },
        );
      } else {
        toast.dismiss("row-selection");
      }
    },
  );

  // Row selection state
  const [internalSelectedRows, setInternalSelectedRows] = useState<
    Set<string | number>
  >(new Set());
  const selectedRows = externalSelectedRows ?? internalSelectedRows;

  //  OPTIMIZACIÓN 2: useCallback para handlers estables
  const handleSelectionChange = useCallback(
    (newSelection: Set<string | number>) => {
      if (onSelectionChange) {
        onSelectionChange(newSelection);
      } else {
        setInternalSelectedRows(newSelection);
      }
    },
    [onSelectionChange],
  );

  //  OPTIMIZACIÓN 3: useCallback para getRowId (evita recrear en cada render)
  const getRowId = useCallback(
    (row: T): string | number => {
      return row[rowIdField] || row.id || row._id;
    },
    [rowIdField],
  );

  // Toast usando useEffectEvent
  useEffect(() => {
    if (!enableRowSelection) return;

    const count = selectedRows.size;
    const translations = {
      message: count === 1 
        ? t("table.selection.selectedCount", { count }) 
        : t("table.selection.selectedCountPlural", { count }),
      clear: t("table.selection.clear"),
      description: t("table.selection.bulkActionsAvailable"),
    };

    showSelectionToast(count, () => handleSelectionChange(new Set()), translations);
  }, [selectedRows.size, enableRowSelection, handleSelectionChange, t]);

  //  OPTIMIZACIÓN 4: Memoizar allRowsSelected
  const allRowsSelected = useMemo(
    () =>
      Array.isArray(data) &&
      data.length > 0 &&
      data.every((row) => selectedRows.has(getRowId(row))),
    [data, selectedRows, getRowId],
  );

  //  OPTIMIZACIÓN 5: useCallback para handleSelectAll
  const handleSelectAll = useCallback(() => {
    if (!Array.isArray(data)) return;

    if (allRowsSelected) {
      handleSelectionChange(new Set());
    } else {
      const allIds = data.map((row) => getRowId(row));
      handleSelectionChange(new Set(allIds));
    }
  }, [data, allRowsSelected, handleSelectionChange, getRowId]);

  //  OPTIMIZACIÓN 6: useCallback para handleRowSelection
  const handleRowSelection = useCallback(
    (rowId: string | number) => {
      const newSelection = new Set(selectedRows);
      if (newSelection.has(rowId)) {
        newSelection.delete(rowId);
      } else {
        newSelection.add(rowId);
      }
      handleSelectionChange(newSelection);
    },
    [selectedRows, handleSelectionChange],
  );

  // Column visibility - ya está optimizado
  const { visibleColumns, toggleColumn, resetToDefaults, isColumnVisible } =
    useColumnVisibility(tableId || "default-table", columns);

  // Vista (tabla | tarjetas) persistida en localStorage por tableId.
  const { viewMode, setViewMode } = useTableViewMode(
    tableId || "default-table",
  );
  const showViewToggle = enableViewToggle;
  const isCardView = showViewToggle && viewMode === "cards";

  //  OPTIMIZACIÓN 7: displayColumns con dependencias correctas
  const displayColumns = useMemo(() => {
    if (!enableColumnVisibility || !tableId) {
      return columns;
    }
    return columns.filter((col) => visibleColumns.includes(col.key));
  }, [enableColumnVisibility, tableId, columns, visibleColumns]);

  // Sorting - ya está bien
  const {
    currentSorts,
    toggleSort,
    getSortedData,
    getSortDirection,
    getSortPriority,
    isMultiSortActive,
  } = useSorting(tableId || "default-table", displayColumns, undefined, true);

  //  OPTIMIZACIÓN 8: sortedData con dependencias correctas
  const sortedData = useMemo(() => {
    if (!Array.isArray(data)) return data;
    return getSortedData(data);
  }, [data, getSortedData]);

  //  OPTIMIZACIÓN 9: tableKey simplificado
  const tableKey = useMemo(
    () =>
      enableColumnVisibility && tableId
        ? `${tableId}-${visibleColumns.join("-")}`
        : "default",
    [enableColumnVisibility, tableId, visibleColumns],
  );

  // UI optimista solo para el numero de pagina activa: la paginacion
  // cambia al instante al hacer click, sin esperar al servidor.
  const [optimisticPage, setOptimisticPage] = useState(
    pagination?.currentPage ?? 1,
  );

  useEffect(() => {
    setOptimisticPage(pagination?.currentPage ?? 1);
  }, [pagination?.currentPage]);

  const handlePageChange = useCallback(
    (page: number) => {
      if (!pagination) return;
      setOptimisticPage(page);
      pagination.onPageChange(page);
    },
    [pagination],
  );

  const effectivePagination = useMemo<
    DataTablePaginationProps | undefined
  >(() => {
    if (!pagination) return undefined;
    return { ...pagination, currentPage: optimisticPage };
  }, [pagination, optimisticPage]);

  //  OPTIMIZACIÓN 10: useEffectEvent para handleColumnHeaderClick
  const handleColumnHeaderClick = useEffectEvent(
    (columnKey: string, event: React.MouseEvent) => {
      const column = displayColumns.find((col) => col.key === columnKey);
      if (!column?.sortable) return;

      const isMultiSort = event.shiftKey || event.ctrlKey;
      toggleSort(columnKey, isMultiSort);
    },
  );

  //  OPTIMIZACIÓN 11: Memoizar getSortIcon
  const getSortIcon = useCallback(
    (columnKey: string) => {
      const direction = getSortDirection(columnKey);
      const priority = getSortPriority(columnKey);

      if (!direction) {
        return <ArrowUpDown className="w-4 h-4 opacity-50" />;
      }

      return (
        <div className="flex items-center gap-1">
          {direction === "asc" ? (
            <ArrowUp className="w-4 h-4 text-primary" />
          ) : (
            <ArrowDown className="w-4 h-4 text-primary" />
          )}
          {isMultiSortActive && priority !== null && (
            <span className="text-xs font-semibold text-primary bg-primary/10 rounded-full w-5 h-5 flex items-center justify-center">
              {priority + 1}
            </span>
          )}
        </div>
      );
    },
    [getSortDirection, getSortPriority, isMultiSortActive],
  );

  // Render de los botones de accion de una tarjeta (compartido entre vista compacta y mobile).
  const renderRowActions = (
    currentActions: TableAction<T>[],
    row: T,
    compact: boolean,
  ) => {
    if (!currentActions || currentActions.length === 0) return null;
    const showOnlyIcon = currentActions.length > 2;
    const btnClass = compact
      ? "h-7 flex-1 min-w-0 px-2"
      : showOnlyIcon
        ? "flex-1 min-w-0 px-0 "
        : "flex-1 sm:flex-none min-w-0 px-3";

    const renderButton = (action: TableAction<T>) => {
      const button = action.alert ? (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant={action.variant || "outline"}
              size="sm"
              className={btnClass}
              title={action.label}
            >
              {action.icon}
              {!showOnlyIcon && (
                <span className="ml-2 truncate text-xs">{action.label}</span>
              )}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{action.alert.title}</AlertDialogTitle>
              <AlertDialogDescription>
                {action.alert.description}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("table.actions.cancel")}</AlertDialogCancel>
              <AlertDialogAction onClick={() => action.onClick(row)}>
                {t("table.actions.confirm")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : (
        <Button
          variant={action.variant || "outline"}
          size="sm"
          onClick={() => action.onClick(row)}
          className={btnClass}
          title={action.label}
        >
          {action.icon}
          {!showOnlyIcon && (
            <span className="ml-2 truncate text-xs">{action.label}</span>
          )}
        </Button>
      );

      if (!compact) {
        return <Fragment key={action.id}>{button}</Fragment>;
      }

      return (
        <Tooltip key={action.id}>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent side="top">
            <p>{action.label}</p>
          </TooltipContent>
        </Tooltip>
      );
    };

    const content = (
      <div
        className={cn(
          "flex border-t border-border",
          compact
            ? "mt-auto gap-1.5 pt-2"
            : `pt-3 ${currentActions.length > 2 ? "gap-2" : "flex-wrap gap-3"}`,
        )}
      >
        {currentActions.map(renderButton)}
      </div>
    );

    if (!compact) return content;

    return <TooltipProvider>{content}</TooltipProvider>;
  };

  // Render de una fila como tarjeta.
  // compact=true → estilo insights (denso, varias por fila en desktop).
  // compact=false → tarjeta amplia de mobile.
  const renderRowCard = (row: T, index: number, compact = false) => {
    const currentActions = rowActions ? rowActions(row) : actions;
    const rowId = getRowId(row);
    const isSelected = selectedRows.has(rowId);

    if (compact) {
      return (
        <Card
          key={row.id || row._id || index}
          variant="outline"
          className={cn(
            "flex flex-col !gap-0 !py-0 light:bg-linear-to-tl from-zinc-100 via-zinc-200 to-zinc-50 hover:bg-zinc-200/70 transition-colors dark:bg-zinc-900/90  dark:hover:bg-zinc-950",
            isSelected && "ring-1 ring-primary/50 bg-accent",
          )}
        >
          <CardContent className="flex flex-1 flex-col gap-2 p-3">
            {enableRowSelection && (
              <div
                className="flex cursor-pointer items-center gap-2"
                onClick={() => handleRowSelection(rowId)}
              >
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => handleRowSelection(rowId)}
                  aria-label={t("table.selection.selectItem", {
                    index: index + 1,
                  })}
                />
              </div>
            )}

            {displayColumns.map((column, colIndex) => {
              const value = column.render
                ? column.render(row[column.key], row)
                : row[column.key];

              if (value === null || value === undefined || value === "")
                return null;

              return (
                <div
                  key={column.key}
                  className="flex items-center justify-between gap-3 text-xs"
                >
                  <span className="shrink-0 text-muted-foreground">
                    {column.label}
                  </span>
                  <div
                    className={
                      colIndex === 0
                        ? "min-w-0 truncate text-right text-sm font-semibold text-foreground"
                        : "min-w-0 truncate text-right font-medium text-foreground"
                    }
                  >
                    {value}
                  </div>
                </div>
              );
            })}

            {renderRowActions(currentActions, row, true)}
          </CardContent>
        </Card>
      );
    }

    return (
      <Card
        key={row.id || row._id || index}
        className={`p-4 ${isSelected ? "ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-950" : ""}`}
      >
        <div className="space-y-3">
          {enableRowSelection && (
            <div
              className="flex items-center gap-2 pb-2 border-b border-border cursor-pointer"
              onClick={() => handleRowSelection(rowId)}
            >
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => handleRowSelection(rowId)}
                aria-label={t("table.selection.selectItem", {
                  index: index + 1,
                })}
              />
              <span className="text-sm text-muted-foreground select-none">
                {isSelected
                  ? t("table.selection.selected")
                  : t("table.selection.select")}
              </span>
            </div>
          )}

          <div className="space-y-3">
            {displayColumns.map((column, colIndex) => {
              const value = column.render
                ? column.render(row[column.key], row)
                : row[column.key];

              if (value === null || value === undefined || value === "")
                return null;

              return (
                <div key={column.key}>
                  {colIndex === 0 ? (
                    <div className="font-bold text-2xl text-foreground truncate pb-3 border-b border-border">
                      {value}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between py-1">
                      <span className="text-sm font-medium text-muted-foreground flex-shrink-0 w-1/3">
                        {column.label}
                      </span>
                      <div className="text-sm text-foreground text-right flex-1 min-w-0 pl-4 flex justify-end items-center">
                        <div className="truncate">{value}</div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {renderRowActions(currentActions, row, false)}
        </div>
      </Card>
    );
  };

  return (
    <>
      <Card
        className={cn("transition-opacity duration-20 w-full hidden sm:block")}
      >
        {(title ||
          description ||
          showViewToggle ||
          (enableColumnVisibility && tableId)) && (
          <CardHeader className="gap-0 pb-2">
            <div className="flex items-center justify-between">
              <div className="space-y-1 flex-1 min-w-0 mr-4 gap-1 flex items-center ">
                {/*{icon ? (
                  <DynamicIcon
                    className="text-foreground stroke-1"
                    size={40}
                    name={icon}
                  />
                ) : (
                  <DynamicIcon
                    className="text-foreground stroke-1"
                    size={40}
                    name={"Table"}
                  />
                )}*/}
                <div>
                  {title && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <CardTitle className="sm:text-md truncate cursor-default">
                            {title}
                          </CardTitle>
                        </TooltipTrigger>
                        <TooltipContent
                          side="bottom"
                          align="start"
                          className="max-w-md"
                        >
                          <p>{title}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {description && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <CardDescription className="text-sm truncate cursor-default">
                            {description}
                          </CardDescription>
                        </TooltipTrigger>
                        <TooltipContent
                          side="bottom"
                          align="start"
                          className="max-w-md"
                        >
                          <p>{description}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              </div>
              {(showViewToggle || (enableColumnVisibility && tableId)) && (
                <div className="flex items-center gap-2">
                  {showViewToggle && (
                    <TableViewToggle
                      viewMode={viewMode}
                      onChange={setViewMode}
                    />
                  )}
                  {enableColumnVisibility && tableId && (
                    <ColumnVisibilityToggle
                      tableId={tableId}
                      columns={columns}
                      visibleColumns={visibleColumns}
                      toggleColumn={toggleColumn}
                      resetToDefaults={resetToDefaults}
                      isColumnVisible={isColumnVisible}
                    />
                  )}
                </div>
              )}
            </div>
          </CardHeader>
        )}
        <CardContent className="flex flex-col pb-0 px-4">
          {isLoading ? (
            <div className="max-h-[75.5dvh] overflow-auto">
              {isCardView ? (
                <SkeletonCardsGrid
                  columnsCount={displayColumns.length}
                  cardsCount={effectivePagination?.pageSize ?? 8}
                />
              ) : (
                <SkeletonTableBody
                  columsNumber={displayColumns.length}
                  rowsNumber={effectivePagination?.pageSize ?? 5}
                  columns={displayColumns}
                  enableRowSelection={enableRowSelection}
                />
              )}
            </div>
          ) : sortedData &&
            Array.isArray(sortedData) &&
            sortedData.length > 0 ? (
            isCardView ? (
              <div className="max-h-[75.5dvh] overflow-y-auto">
                <div className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-3 py-1">
                  {sortedData.map((row, index) =>
                    renderRowCard(row, index, true),
                  )}
                </div>
              </div>
            ) : (
              // Scroll nativo en ambos ejes: la tabla crece a su contenido
              // (w-max) y desborda horizontalmente cuando hay muchas columnas.
              <div className="max-h-[75.5dvh] w-full overflow-auto">
                <Table
                  key={tableKey}
                  className={cn("w-max min-w-full", className)}
                >
                  <DataTableHeader
                    displayColumns={displayColumns}
                    enableRowSelection={enableRowSelection}
                    allRowsSelected={allRowsSelected}
                    handleSelectAll={handleSelectAll}
                    handleColumnHeaderClick={handleColumnHeaderClick}
                    getSortIcon={getSortIcon}
                    hasActions={(actions?.length ?? 0) > 0 || !!rowActions}
                  />

                  <DataTableBody
                    sortedData={sortedData}
                    selectedRows={selectedRows}
                    enableRowSelection={enableRowSelection}
                    displayColumns={displayColumns}
                    handleRowSelection={handleRowSelection}
                    getRowId={getRowId}
                    rowActions={rowActions}
                  />
                </Table>
              </div>
            )
          ) : (
            <Empty className="py-12">
              <EmptyHeader className="gap-0">
                <EmptyMedia>
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center text-muted-foreground">
                    {emptyState?.icon ?? (
                      <Inbox className="size-12 opacity-60" />
                    )}
                  </div>
                </EmptyMedia>
                <EmptyTitle>
                  {emptyState?.title || t("table.empty.title")}
                </EmptyTitle>
                <EmptyDescription>
                  {emptyState?.description || t("table.empty.description")}
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          )}

          {/* Paginacion: footer fijo fuera del scroll, se mantiene durante la
              carga para que no desaparezca/salte el layout. */}
          {effectivePagination && (
            <div className="flex-shrink-0 pt-2">
              <AdvancedPagination
                currentPage={effectivePagination.currentPage}
                totalCount={effectivePagination.totalCount}
                totalCountIsLowerBound={
                  effectivePagination.totalCountIsLowerBound
                }
                pageSize={effectivePagination.pageSize}
                onPageChange={handlePageChange}
                showInfo={true}
                compactMobile={false}
                isLoading={isLoading}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Vista de cards para mobile sin borde principal */}
      <div className="sm:hidden flex-1 min-h-0 flex flex-col">
        {isLoading ? (
          <SkeletonCardsMobile
            columnsCount={displayColumns.length}
            cardsCount={effectivePagination?.pageSize ?? 5}
          />
        ) : sortedData && Array.isArray(sortedData) && sortedData.length > 0 ? (
          <div className="flex flex-col min-h-0 flex-1">
            {(title ||
              description ||
              enableRowSelection ||
              (enableColumnVisibility && tableId)) && (
              <div className="mb-3">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center justify-between gap-2">
                    {title && (
                      <div className="font-bold text-lg truncate">{title}</div>
                    )}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {enableRowSelection && (
                        <Checkbox
                          checked={allRowsSelected}
                          onCheckedChange={handleSelectAll}
                          aria-label={t("table.selection.selectAll")}
                        />
                      )}
                      {enableRowSelection && (
                        <span className="text-xs text-muted-foreground">
                          {selectedRows.size} / {sortedData.length}
                        </span>
                      )}
                      {enableColumnVisibility && tableId && (
                        <ColumnVisibilityToggle
                          tableId={tableId}
                          columns={columns}
                          visibleColumns={visibleColumns}
                          toggleColumn={toggleColumn}
                          resetToDefaults={resetToDefaults}
                          isColumnVisible={isColumnVisible}
                          showLabel={false}
                        />
                      )}
                    </div>
                  </div>

                  {description && (
                    <div className="text-xs text-muted-foreground truncate mt-1">
                      {description}
                    </div>
                  )}
                </div>
              </div>
            )}

            <ScrollArea className="min-h-0 flex-1">
              <div className="space-y-3 pb-4">
                {sortedData.map((row, index) => renderRowCard(row, index))}
              </div>
            </ScrollArea>
          </div>
        ) : (
          <Empty className="py-6 px-4 gap-3">
            <EmptyHeader className="gap-1">
              <EmptyMedia className="mb-0">
                <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center text-muted-foreground">
                  {emptyState?.icon ?? <Inbox className="size-10 opacity-60" />}
                </div>
              </EmptyMedia>
              <EmptyTitle className="text-base">
                {emptyState?.title || t("table.empty.title")}
              </EmptyTitle>
              <EmptyDescription>
                {emptyState?.description || t("table.empty.description")}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        )}

        {effectivePagination && (
          <div className="flex-shrink-0 mt-4 pt-4 border-t border-border">
            <AdvancedPagination
              currentPage={effectivePagination.currentPage}
              totalCount={effectivePagination.totalCount}
              totalCountIsLowerBound={
                effectivePagination.totalCountIsLowerBound
              }
              pageSize={effectivePagination.pageSize}
              onPageChange={handlePageChange}
              showInfo={true}
              compactMobile={true}
              isLoading={isLoading}
            />
          </div>
        )}
      </div>
    </>
  );
}
